import { CanceledError, getAdapter } from "axios";

// the error the axios adapters reject with when the request is cancelled before sending
const cancelReason = (config) =>
  config.cancelToken?.reason ||
  (config.signal?.aborted ? new CanceledError(null, config) : null);

/**
 * Limit the number of requests the axios instance sends at the same time.
 * Wraps the adapter rather than using interceptors, so a slot is held only
 * while the request is actually on the wire and is always released.
 * A request cancelled while queued is rejected right away and never sent.
 * @param {Object} axios - axios instance
 * @param {number} maxConcurrent - maximum number of requests in flight
 */
const limitConcurrency = (axios, maxConcurrent) => {
  if (!Number.isInteger(maxConcurrent) || maxConcurrent < 1) {
    throw new TypeError("maxConcurrent must be an integer >= 1");
  }
  const adapter = getAdapter(axios.defaults.adapter);
  // FIFO queue; `head` makes dequeue O(1), the consumed part is dropped from time to time
  let queue = [];
  let head = 0;
  let active = 0;

  const next = () => {
    while (active < maxConcurrent && head < queue.length) {
      const { config, resolve, reject, unsubscribe } = queue[head];
      queue[head++] = undefined;
      if (head > 1024 && head * 2 > queue.length) {
        queue = queue.slice(head);
        head = 0;
      }
      // once sent, the adapter handles cancellation itself
      unsubscribe();
      // a cancelled request is skipped; it is usually rejected already, but CancelToken
      // notifies its listeners asynchronously
      const reason = cancelReason(config);
      if (reason) {
        reject(reason);
        continue;
      }
      active++;
      // async wrapper turns a synchronous throw into a rejection, so the slot is always freed
      const result = (async () => adapter(config))();
      resolve(result);
      result
        .catch(() => {})
        .finally(() => {
          active--;
          next();
        });
    }
  };

  axios.defaults.adapter = (config) =>
    new Promise((resolve, reject) => {
      // like the axios adapters, reject as soon as the request is cancelled
      const onCancel = () => reject(cancelReason(config));
      const unsubscribe = () => {
        config.cancelToken?.unsubscribe(onCancel);
        config.signal?.removeEventListener("abort", onCancel);
      };
      config.cancelToken?.subscribe(onCancel);
      config.signal?.addEventListener("abort", onCancel);
      queue.push({ config, resolve, reject, unsubscribe });
      next();
    });
};

export { limitConcurrency };
